import type { TransferActionReference } from '@super/node-graph-ui'
import { validateName, validateTransferParameters } from '@super/node-graph-ui'

interface TransferStep {
  id: string
  type: string
  workflowActionId?: string
}

interface CanvasStateLike {
  steps: Array<{ id: string; type: string } & Record<string, unknown>>
}

/**
 * Walks every transfer node on the canvas and returns the id of the first
 * one whose referenced WorkflowAction fails the same checks the edit form
 * runs on save: Name must be non-empty and recipients must be set.
 *
 * A transfer node with no attached workflowActionId counts as invalid
 * (fresh draft that was never saved). A reference still loading (undefined)
 * is skipped — the server-side validator catches it if it slips through.
 */
export function findInvalidTransferNode(
  state: CanvasStateLike,
  referencesById: Record<string, TransferActionReference | null | undefined>,
): string | null {
  for (const rawStep of state.steps) {
    const step = rawStep as TransferStep
    if (step.type !== 'transfer') continue
    const workflowActionId = step.workflowActionId
    if (!workflowActionId) return step.id
    const ref = referencesById[workflowActionId]
    if (!ref) continue
    if (validateName(ref.name ?? '').nameError) return step.id
    if (ref.parameters == null) return step.id
    if (validateTransferParameters(ref.parameters).issue !== null) {
      return step.id
    }
  }
  return null
}
